import { EmissionValueUnit } from '@api-client/api'
import { Box } from '@mui/material'
import { StyledDivider } from '@views/Registration/styles'
import { FC, useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import FormSection from './Form'
import { calculateTotalEmissions } from './helpers/calculation'
import { defaultFormValues, formConfig } from './helpers/config'
import { getData } from './helpers/transform'
import Results from './Results'

export interface TransportFormValues {
	category: string
	subcategory: string
	subsubcategory: string
	emissionfactor: string
	weight: number
	distance: string | number | null
}

export interface EnergyFormValues {
	category: string
	subcategory: string
	subsubcategory: string
	emissionfactor: string
	amount: string | number | null
}

export interface RawMaterialPackagingFormValues {
	category: string
	subcategory: string
	subsubcategory: string
	emissionfactor: string
	weight: string | number | null
	transportcategory: string
	transportsubcategory: string
	transportsubsubcategory: string
	transportemissionfactor: string
	distance: string | number | null
}

export interface FormValues {
	transportBetweenSites: readonly TransportFormValues[]
	transport: readonly TransportFormValues[]
	energy: readonly EnergyFormValues[]
	rawmaterial: readonly RawMaterialPackagingFormValues[]
	packaging: readonly RawMaterialPackagingFormValues[]
}

export interface EmissionResults {
	material: number
	production: number
	distribution: number
}

const Calculator: FC = () => {
	const [results, setResults] = useState<EmissionResults>({
		material: 0,
		production: 0,
		distribution: 0,
	})

	const methods = useForm<FormValues>({
		defaultValues: {
			rawmaterial: [defaultFormValues['rawmaterial']],
			packaging: [],
			energy: [],
			transport: [],
			transportBetweenSites: [],
		},
	})

	const {
		formState: { isDirty },
		clearErrors,
		handleSubmit,
		reset,
	} = methods

	const onSubmit = useCallback((data: FormValues) => {
		const optionsdata = getData()
		const totalemissions = calculateTotalEmissions(optionsdata, data)

		setResults(totalemissions)

		if (Object.values(totalemissions).every(val => val === 0)) {
			return
		}
	}, [])

	// TODO(pascal): Avoid submitting inside useEffect.
	useEffect(() => {
		handleSubmit(onSubmit)()
		clearErrors()
		reset({}, { keepValues: true })
	}, [isDirty, handleSubmit, clearErrors, reset, onSubmit])

	return (
		<Box sx={{ display: 'flex', gap: '1.5rem' }}>
			<FormProvider {...methods}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<Box pb={0.5}>
						<StyledDivider />
					</Box>
					{Object.keys(formConfig).map(key => (
						<FormSection key={key} section={key as keyof FormValues} />
					))}
				</form>
			</FormProvider>

			<Results
				data={results}
				counts={[
					methods.getValues('rawmaterial').length,
					methods.getValues('packaging').length,
					methods.getValues('energy').length,
				]}
				unit={EmissionValueUnit.KGCO2_EQ_KG}
			/>
		</Box>
	)
}

export { Calculator }
